import {CommonModule} from '@angular/common';
import {Component, computed, DestroyRef, inject, OnInit, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {InfiniteScrollModule} from 'ngx-infinite-scroll';
import {combineLatest, map, Subscription} from 'rxjs';
import {PureOriginalMatchPage, PureOriginalMatchRow, PureOriginalMatchSort, PureOriginalMatchTarget} from '../../dashboard-analytics/models/dashboard-statistics.model';
import {DashboardStatisticsService} from '../../dashboard-analytics/services/dashboard-statistics.service';
import {DashboardPeriodService} from '../../dashboard/services/dashboard-period.service';

type MatchRequest = {
  target: PureOriginalMatchTarget | null;
  startDate: string;
  endDate: string;
  pageNumber: number;
  pageSize: number;
  sortAttribute: PureOriginalMatchSort;
  sortDirection: 'asc' | 'desc';
};

@Component({
  selector: 'app-dashboard-matches',
  standalone: true,
  imports: [CommonModule, RouterLink, InfiniteScrollModule],
  templateUrl: './dashboard-matches.component.html',
  styleUrls: ['./dashboard-matches.component.css']
})
export class DashboardMatchesComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly stats = inject(DashboardStatisticsService);
  private readonly period = inject(DashboardPeriodService);
  private readonly destroyRef = inject(DestroyRef);
  private currentRequest: MatchRequest | null = null;
  private pageRequest?: Subscription;
  private failedPage: number | null = null;

  readonly target = signal<PureOriginalMatchTarget>('missing');
  readonly page = signal<PureOriginalMatchPage | null>(null);
  readonly rows = signal<PureOriginalMatchRow[]>([]);
  readonly loading = signal(false);
  readonly error = signal('');
  readonly rangeLabel = signal('');
  readonly pageNumber = signal(0);
  readonly pageSize = signal(50);
  readonly sortAttribute = signal<PureOriginalMatchSort>('pureOriginalBaseId');
  readonly sortDirection = signal<'asc' | 'desc'>('desc');
  readonly hasMore = computed(() => {
    const page = this.page();
    return !!page && !page.last && page.number + 1 < page.totalPages &&
      page.content.length > 0 && this.rows().length < page.totalElements;
  });
  readonly titles = {missing: 'Missing Match', partial: 'Partial Match', successful: 'Successful Match'};

  ngOnInit(): void {
    this.destroyRef.onDestroy(() => this.pageRequest?.unsubscribe());
    combineLatest([this.route.paramMap, this.route.queryParamMap]).pipe(
      map(([route, params]): MatchRequest => {
        const target = route.get('target');
        const matchTarget: PureOriginalMatchTarget | null = target === 'missing' || target === 'partial' || target === 'successful' ? target : null;
        const sort = params.get('sortAttribute');
        const sortAttribute: PureOriginalMatchSort = sort === 'tuningName' || sort === 'timesRequested' ? sort : 'pureOriginalBaseId';
        const sortDirection: 'asc' | 'desc' = params.get('sortDirection') === 'asc' ? 'asc' : 'desc';
        return {
          target: matchTarget,
          startDate: params.get('startDate') || this.period.currentStart(),
          endDate: params.get('endDate') || this.period.currentEnd(),
          pageNumber: this.validInteger(params.get('pageNumber'), 0, 0),
          pageSize: this.validInteger(params.get('pageSize'), 50, 1, 100),
          sortAttribute, sortDirection
        };
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(request => {
      this.pageRequest?.unsubscribe();
      this.currentRequest = request;
      this.failedPage = null;
      this.page.set(null);
      this.rows.set([]);
      this.loading.set(false);
      this.error.set('');
      if (request.target) this.target.set(request.target);
      this.pageNumber.set(request.pageNumber);
      this.pageSize.set(request.pageSize);
      this.sortAttribute.set(request.sortAttribute);
      this.sortDirection.set(request.sortDirection);
      this.rangeLabel.set(`${request.startDate.replace('T', ' ')} – ${request.endDate.replace('T', ' ')}`);
      if (!request.target) {
        this.error.set('Unknown match status. Return to the dashboard and select a match card.');
        return;
      }
      this.loadPage(request.pageNumber);
    });
  }

  retry(): void {
    if (this.failedPage !== null) this.loadPage(this.failedPage);
  }

  onScroll(): void {
    if (!this.loading() && !this.error() && this.hasMore()) this.loadPage(this.pageNumber() + 1);
  }

  changePageSize(event: Event): void {
    this.updateQuery({pageNumber: 0, pageSize: Number((event.target as HTMLSelectElement).value)});
  }

  sortBy(sortAttribute: PureOriginalMatchSort): void {
    if (this.loading()) return;
    this.updateQuery({pageNumber: 0, sortAttribute,
      sortDirection: this.sortAttribute() === sortAttribute && this.sortDirection() === 'desc' ? 'asc' : 'desc'});
  }

  ariaSort(column: PureOriginalMatchSort): 'ascending' | 'descending' | 'none' {
    return this.sortAttribute() !== column ? 'none' : this.sortDirection() === 'asc' ? 'ascending' : 'descending';
  }

  openDetails(row: PureOriginalMatchRow): void {
    if (!row.pureOriginalBaseId) return;
    this.router.navigate(['/app/pure-originals/pure-original-details'], {
      queryParams: {pureOriginalId: row.pureOriginalBaseId}
    });
  }

  private loadPage(pageNumber: number): void {
    const request = this.currentRequest;
    if (!request?.target || this.loading()) return;
    this.loading.set(true);
    this.error.set('');
    this.pageRequest = this.stats.getPureOriginalMatches(request.target, request.startDate, request.endDate,
      pageNumber, request.pageSize, request.sortAttribute, request.sortDirection).subscribe({
      next: page => {
        this.rows.update(rows => pageNumber === request.pageNumber ? page.content : [...rows, ...page.content]);
        this.page.set(page);
        this.pageNumber.set(page.number);
        this.failedPage = null;
        this.loading.set(false);
      },
      error: () => {
        this.failedPage = pageNumber;
        this.error.set('Could not load Pure Original matches. Please try again.');
        this.loading.set(false);
      }
    });
  }

  private updateQuery(queryParams: Record<string, string | number>): void {
    this.router.navigate([], {relativeTo: this.route, queryParams, queryParamsHandling: 'merge'});
  }

  private validInteger(value: string | null, fallback: number, min: number, max = Number.MAX_SAFE_INTEGER): number {
    const parsed = value === null ? NaN : Number(value);
    return Number.isSafeInteger(parsed) && parsed >= min && parsed <= max ? parsed : fallback;
  }
}
